import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { StatusBadge, RiskPill, fmtDate, fmtCurrency } from "../lib/status";
import { ArrowLeft, MapPin, Building2, Info as InfoIcon, RotateCcw, Star, Receipt, MessageCircle } from "lucide-react";
import { STATUS_EXPLAINERS } from "@/content/legal";
import ConfirmationHistoryPanel from "../components/ConfirmationHistoryPanel";
import CustomerDriversCard from "../components/renter-drivers/CustomerDriversCard";
import CustomerSettlementCard from "../components/rental-lifecycle/CustomerSettlementCard";
import BookingChatPanel from "../components/booking-chat/BookingChatPanel";
import RenterVehicleDetailModal from "../components/vehicle-details/RenterVehicleDetailModal";
import BookingConditionRecordModal from "../components/condition-record/BookingConditionRecordModal";

const REVIEW_STATUS_LABEL = {
  pending: "Submitted: awaiting the business's publish", published: "Published", rejected: "Not published",
};

/**
 * `preloaded`: optional sandboxed-demo hook (used by the homepage
 * walkthrough), matching the same pattern already established on
 * RentalProfile/CustomerConfirmation. Skips the real
 * `/customer/bookings/:id` fetch when provided. Real behavior is
 * unchanged when absent.
 */
export default function CustomerBookingDetail({ preloaded = null, onContactBusiness = null } = {}) {
  const { id } = useParams();
  const [data, setData] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);

  useEffect(() => {
    if (preloaded) return;
    api.get(`/customer/bookings/${id}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [id, preloaded]);

  if (loading) return <div className="text-slate-500" data-testid="portal-bd-loading">Loading…</div>;
  if (!data) return <div className="text-slate-500">Not found.</div>;

  const { booking: b, company, vehicle, confirmation_versions, deposit, review_status } = data;

  const bookAgainHref = company?.id ? `/business/${company.id}?` + new URLSearchParams({
    name: b.customer_name || "", email: b.customer_email || "", phone: b.customer_phone || "",
    pickup: b.pickup_location || "", vehicle: vehicle?.id || "", repeat_of: b.id,
  }).toString() : null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="portal-bd-page">
      <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-slate-700 hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to my bookings
      </Link>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-bold">{b.booking_reference}</h1>
              <StatusBadge status={b.status} isHold={b.is_hold} changeRequested={b.change_requested}
                           cancelledBy={b.cancelled_by} cancellationReason={b.cancellation_reason}
                           reconfirmationRequired={b.reconfirmation_required} />
              {b.risk_status && b.risk_status !== "ok" && <RiskPill risk={b.risk_status} />}
            </div>
            <p className="text-sm text-slate-600 mt-1 inline-flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" /> {company?.name} {company?.city ? `· ${company.city}` : ""}
            </p>
            {(STATUS_EXPLAINERS[b.status] || (b.risk_status && STATUS_EXPLAINERS[b.risk_status])) && (
              <div className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-start gap-2"
                   data-testid="portal-bd-status-explainer">
                <InfoIcon className="w-3.5 h-3.5 mt-0.5 text-slate-500 flex-shrink-0" />
                <span>
                  {STATUS_EXPLAINERS[b.risk_status] || STATUS_EXPLAINERS[b.status]}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowRecordModal(true)}
              data-testid="portal-bd-view-record-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium"
            >
              <Receipt className="w-4 h-4" />
              Booking &amp; Condition Record
            </button>
            {b.status === "completed" && bookAgainHref && (
              <Link
                to={bookAgainHref}
                data-testid="portal-bd-book-again"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium"
              >
                <RotateCcw className="w-4 h-4" />
                Book again
              </Link>
            )}
          </div>
        </div>

        <dl className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
          <Info label="Vehicle" value={
            vehicle?.name ? (
              <span className="inline-flex items-center gap-2 flex-wrap">
                {vehicle.name}
                <button
                  type="button"
                  onClick={() => setShowVehicleModal(true)}
                  data-testid="portal-bd-view-vehicle-btn"
                  className="text-xs font-medium px-2 py-0.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                >
                  View details →
                </button>
              </span>
            ) : "-"
          } />
          <Info label="Plate" value={vehicle?.plate_number || "-"} />
          <Info label="Start" value={fmtDate(b.start_datetime)} />
          <Info label="End" value={fmtDate(b.end_datetime)} />
          <Info label="Price" value={fmtCurrency(b.price)} />
          <Info label="Security deposit" value={fmtCurrency(b.deposit_required)} />
          {b.pickup_location && <Info label="Pickup" value={<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.pickup_location}</span>} />}
          {b.return_location && <Info label="Return" value={<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.return_location}</span>} />}
        </dl>

        {b.customer_visible_notes && (
          <div className="mt-6 rounded-lg bg-emerald-50/60 border border-emerald-200 p-4 text-sm text-emerald-900 whitespace-pre-line">
            {b.customer_visible_notes}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-semibold">Business contact</h2>
        <dl className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
          <Info label="Business" value={company?.name || "-"} />
          <Info label="City" value={company?.city || "-"} />
          <Info label="Email" value={company?.email || "-"} />
          <Info label="Phone" value={company?.phone || "-"} />
        </dl>
        {onContactBusiness && (
          <button
            type="button"
            onClick={onContactBusiness}
            data-testid="portal-bd-contact-business"
            className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#1B4332] px-4 py-2 text-sm font-semibold text-white hover:bg-[#143027]"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" /> Contact Booking Provider
          </button>
        )}
      </div>

      {deposit && deposit.status !== "not_required" && (deposit.required > 0 || deposit.status !== "pending") && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="portal-bd-deposit">
          <h2 className="font-display text-lg font-semibold">Security deposit</h2>
          <dl className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
            <Info label="Status" value={<span className="capitalize">{(deposit.status || "pending").replace(/_/g, " ")}</span>} />
            <Info label="Required" value={fmtCurrency(deposit.required)} />
            <Info label="Collected" value={fmtCurrency(deposit.collected)} />
            {deposit.outstanding_to_collect > 0 && <Info label="Outstanding" value={fmtCurrency(deposit.outstanding_to_collect)} />}
            {deposit.refunded > 0 && <Info label="Refunded" value={fmtCurrency(deposit.refunded)} />}
            {deposit.withheld > 0 && <Info label="Withheld" value={fmtCurrency(deposit.withheld)} />}
            {deposit.withheld > 0 && deposit.withheld_reason && <Info label="Reason" value={deposit.withheld_reason} />}
          </dl>
        </div>
      )}

      {review_status?.invited && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="portal-bd-review-status">
          <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
            <Star className="w-4 h-4 text-[#1B4332]" /> Review
          </h2>
          <p className="mt-2 text-sm text-slate-700">
            {review_status.submitted
              ? (REVIEW_STATUS_LABEL[review_status.status] || "Submitted")
              : "You've been invited to leave a review for this booking: check your email for the link."}
          </p>
        </div>
      )}

      {!preloaded && (b.rental_service_type || "self_drive") === "self_drive" && (
        <CustomerDriversCard basePath={`/customer/bookings/${b.id}`} renterName={b.customer_name}
                             readOnly={["completed", "cancelled"].includes(b.status)} />
      )}

      {!preloaded && <CustomerSettlementCard basePath={`/customer/rentals/${b.id}`} />}

      {confirmation_versions?.length > 0 && (
        <ConfirmationHistoryPanel bookingId={b.id} versions={confirmation_versions} />
      )}

      {/* BookingChatPanel has no preloaded/demo hook of its own and always
          fetches for real: skip it entirely in demo mode (same reasoning
          as CustomerTimelineCard's `disabled` gate in CustomerConfirmation.jsx)
          rather than have it hit the real API with a fabricated booking id. */}
      {!preloaded && <BookingChatPanel bookingId={b.id} viewerType="customer" />}

      {showVehicleModal && (
        <RenterVehicleDetailModal source={{ mode: "portal", bookingId: b.id }} onClose={() => setShowVehicleModal(false)} />
      )}

      {showRecordModal && (
        <BookingConditionRecordModal source={{ mode: "portal", bookingId: b.id }} onClose={() => setShowRecordModal(false)} />
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-900">{value}</dd>
    </div>
  );
}
